import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../api/client';
import {
  EGE_YEAR_OPTIONS,
  FINANCE_OPTIONS,
  GOAL_OPTIONS,
  GROUP_OPTIONS,
  HOMEWORK_OPTIONS,
  SCORE_OPTIONS,
  WORKLOAD_OPTIONS,
  WORK_STYLE_QUESTIONS,
  buildWorkStyleTips,
  hasIncompleteWorkStyle,
  needsEgeDetails,
  optionLabel,
} from '../questionnaire/options';
import { studentLabel } from '../utils/studentLabel';
import { useStudentTabIdentity } from '../hooks/useTabIdentity';
import { useAuth } from '../context/AuthContext';

function AnswerRow({ label, value }) {
  return (
    <div className="questionnaire-readonly-row">
      <div className="questionnaire-readonly-label">{label}</div>
      <div className="questionnaire-readonly-value">{value || '—'}</div>
    </div>
  );
}

export default function TeacherStudentQuestionnairePage() {
  const { studentId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const fromNewUsers = location.state?.from === 'new-users';
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teacherComment, setTeacherComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/teacher/students/${studentId}/questionnaire/`);
        if (cancelled) return;
        setData(res.data);
        setTeacherComment(res.data?.teacher_comment || '');
        if (isSiteAdmin && studentId) {
          api.post(`/teacher/new-users/${studentId}/read/`).catch(() => {});
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Не удалось загрузить анкету');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, isSiteAdmin]);

  useStudentTabIdentity(data?.student ?? null, 'анкета');

  const workStyleTips = useMemo(
    () => (data?.filled && !hasIncompleteWorkStyle(data) ? buildWorkStyleTips(data) : []),
    [data],
  );

  async function saveComment(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.patch(`/teacher/students/${studentId}/questionnaire/`, {
        teacher_comment: teacherComment,
      });
      setData((prev) => ({ ...prev, ...res.data, filled: true }));
      setSuccess('Комментарий сохранён');
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить комментарий');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка анкеты...</p>
      </div>
    );
  }

  const student = data?.student;
  const studentName = student ? studentLabel(student) : '';
  const canEditComment = Boolean(data?.can_edit_comment);

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <p>
          {fromNewUsers ? (
            <Link to="/teacher/new-users" className="back-link">← Новые пользователи</Link>
          ) : (
            <Link to={`/teacher/students/${studentId}`} className="back-link">← К работе с учеником</Link>
          )}
        </p>
        <h1>Анкета{studentName ? `: ${studentName}` : ''}</h1>
        {student?.username && <p className="page-subtitle">@{student.username}</p>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!data?.filled ? (
        <p className="text-secondary">Ученик ещё не заполнил анкету.</p>
      ) : (
        <>
          {workStyleTips.length > 0 && (
            <div className="questionnaire-tips">
              <h2>Режим на занятии</h2>
              <p className="questionnaire-hint">
                Краткая шпаргалка по ответам блока 8 — не диагноз, а стартовые настройки.
              </p>
              <ul className="questionnaire-tips-list">
                {workStyleTips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          {hasIncompleteWorkStyle(data) && (
            <div className="alert alert-warning">
              Блок «как удобнее работать на занятии» ещё не заполнен — попросите ученика обновить анкету.
            </div>
          )}

          <div className="questionnaire-readonly">
            <AnswerRow
              label="1. Цель подготовки"
              value={
                data.goal === 'other' && data.goal_other
                  ? `Другое: ${data.goal_other}`
                  : optionLabel(GOAL_OPTIONS, data.goal)
              }
            />
            {needsEgeDetails(data.goal) && (
              <>
                <AnswerRow
                  label="1.2. Год сдачи ЕГЭ"
                  value={
                    data.ege_year === 'other' && data.ege_year_other
                      ? data.ege_year_other
                      : optionLabel(EGE_YEAR_OPTIONS, data.ege_year)
                  }
                />
                <AnswerRow
                  label="1.3. Целевые баллы"
                  value={optionLabel(SCORE_OPTIONS, data.target_score)}
                />
              </>
            )}
            <AnswerRow
              label="2. Опыт программирования"
              value={data.programming_experience}
            />
            <AnswerRow
              label="3. Опыт решения заданий школьной информатики"
              value={data.school_informatics_experience}
            />
            <AnswerRow
              label="4. Отношение к ДЗ"
              value={optionLabel(HOMEWORK_OPTIONS, data.homework_attitude)}
            />
            <AnswerRow
              label="5. Загруженность"
              value={optionLabel(WORKLOAD_OPTIONS, data.workload)}
            />
            <AnswerRow
              label="6. Групповые занятия"
              value={optionLabel(GROUP_OPTIONS, data.group_attitude)}
            />
            <AnswerRow
              label="7. Финансовые возможности"
              value={optionLabel(FINANCE_OPTIONS, data.finance)}
            />

            <div className="questionnaire-readonly-row">
              <div className="questionnaire-readonly-label">
                8. Как удобнее работать на занятии
              </div>
            </div>
            {WORK_STYLE_QUESTIONS.map((q) => (
              <AnswerRow
                key={q.field}
                label={q.legend}
                value={optionLabel(q.options, data[q.field])}
              />
            ))}

            <AnswerRow label="Комментарий ученика" value={data.comment} />
          </div>

          {canEditComment ? (
            <form className="questionnaire-teacher-note" onSubmit={saveComment}>
              <h2>Комментарий преподавателя</h2>
              <p className="questionnaire-hint">Виден только вам, ученик его не видит.</p>
              {success && <div className="alert alert-success">{success}</div>}
              <textarea
                className="form-input"
                rows={5}
                value={teacherComment}
                onChange={(e) => setTeacherComment(e.target.value)}
              />
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить комментарий'}
                </button>
              </div>
            </form>
          ) : data.teacher_comment ? (
            <div className="questionnaire-teacher-note">
              <h2>Комментарий преподавателя</h2>
              <p className="text-secondary">{data.teacher_comment}</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
